export type ProbabilityExperimentType = 'coin' | 'die' | 'basketball' | 'lottery';

export interface OutcomeDefinition {
  readonly label: string;
  readonly value: number;
  readonly svgType: string;
  readonly probabilityNumerator: number;
  readonly probabilityDenominator: number;
}

export interface ProbabilityExperimentDefinition {
  readonly type: ProbabilityExperimentType;
  readonly name: string;
  readonly description: string;
  readonly outcomes: readonly OutcomeDefinition[];
  readonly sampleSpaceDescription: string;
  readonly tabSvgType: string;
  readonly note?: string;
}

export const COIN_FLIP: ProbabilityExperimentDefinition = {
  type: 'coin',
  name: 'Coin Flip',
  description: 'Flip a fair coin',
  sampleSpaceDescription: 'U = {0, 1}, where Tails = 0 and Heads = 1. A coin flip has two equally likely outcomes.',
  tabSvgType: 'coin-heads',
  outcomes: [
    { label: 'Heads', value: 1, svgType: 'coin-heads', probabilityNumerator: 1, probabilityDenominator: 2 },
    { label: 'Tails', value: 0, svgType: 'coin-tails', probabilityNumerator: 1, probabilityDenominator: 2 },
  ],
};

export const DIE_ROLL: ProbabilityExperimentDefinition = {
  type: 'die',
  name: 'Roll a Die',
  description: 'Roll a fair six-sided die',
  sampleSpaceDescription: 'U = {1, 2, 3, 4, 5, 6}, the six faces of a standard die.',
  tabSvgType: 'die-6',
  outcomes: [
    { label: '1', value: 1, svgType: 'die-1', probabilityNumerator: 1, probabilityDenominator: 6 },
    { label: '2', value: 2, svgType: 'die-2', probabilityNumerator: 1, probabilityDenominator: 6 },
    { label: '3', value: 3, svgType: 'die-3', probabilityNumerator: 1, probabilityDenominator: 6 },
    { label: '4', value: 4, svgType: 'die-4', probabilityNumerator: 1, probabilityDenominator: 6 },
    { label: '5', value: 5, svgType: 'die-5', probabilityNumerator: 1, probabilityDenominator: 6 },
    { label: '6', value: 6, svgType: 'die-6', probabilityNumerator: 1, probabilityDenominator: 6 },
  ],
};

export const BASKETBALL_SHOT: ProbabilityExperimentDefinition = {
  type: 'basketball',
  name: 'Basketball Shot',
  description: 'Shoot a free throw',
  sampleSpaceDescription: 'U = {0, 1}, where Miss = 0 and Make = 1. The probability of making a free throw is 3/4.',
  tabSvgType: 'basketball-make',
  outcomes: [
    { label: 'Make', value: 1, svgType: 'basketball-make', probabilityNumerator: 3, probabilityDenominator: 4 },
    { label: 'Miss', value: 0, svgType: 'basketball-miss', probabilityNumerator: 1, probabilityDenominator: 4 },
  ],
};

export const LOTTERY_GAME: ProbabilityExperimentDefinition = {
  type: 'lottery',
  name: 'Lottery',
  description: 'Pay $2 for a 1-in-10 chance to win $20',
  sampleSpaceDescription:
    'U = {0, 18}, where Lose = 0 (net −$2) and Win = 18 (net +$18). You pay $2 for a 1-in-10 chance to win $20.',
  tabSvgType: 'lottery-win',
  outcomes: [
    { label: 'Win (+$18)', value: 18, svgType: 'lottery-win', probabilityNumerator: 1, probabilityDenominator: 10 },
    { label: 'Lose (−$2)', value: 0, svgType: 'lottery-lose', probabilityNumerator: 9, probabilityDenominator: 10 },
  ],
  note: 'Fair game: E(X) = 0',
};

export interface ExperimentTrialResult {
  readonly trialNumber: number;
  readonly outcome: OutcomeDefinition;
  readonly isSuccess: boolean;
}

export interface ExperimentRun {
  readonly runNumber: number;
  readonly trials: number;
  readonly results: ExperimentTrialResult[];
  readonly successCount: number;
  readonly lossCount: number;
}

export const ALL_EXPERIMENTS: readonly ProbabilityExperimentDefinition[] = [
  COIN_FLIP,
  DIE_ROLL,
  BASKETBALL_SHOT,
  LOTTERY_GAME,
];
